'use client';

import type { ReactNode, Ref } from 'react';
import { cn } from '@/lib/utils';
import {
  useListHeaderFill,
  useListRowRhythm
} from '@/components/shared/list-row';
import { HeaderActions } from '@/components/shared/header-actions';

/**
 * Shared chrome for the top-level pages that render on the global sidebar shell
 * (Scribe, Intake, Templates) — LF-4733. One page template so those surfaces
 * read as one product: a 20px title + subtitle header with a top-right
 * Help Centre slot, and a bounded content column. New such pages should
 * adopt this shell rather than re-implementing the chrome.
 *
 * All four top-level pages now share ONE shell (`LayoutWrapper`) for their
 * chrome — rail, scrollbar, white region (LF-5279). This template adds the
 * header + bounded content column on top of that shell for Scribe/Intake/
 * Templates; the Matters dashboard views provide their own column (bespoke
 * list header + filters) but from the SAME canonical `max-w-content` token
 * (LF-5142), so the content width can't drift.
 */

/** Bounded content column shared by the header and body — the canonical
 * app-wide `max-w-content` (LF-5142), matching the Matters dashboard views. */
export const PAGE_SHELL_WIDTH = 'mx-auto w-full max-w-content';

interface PageHeaderRowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
}

/**
 * The header row itself (title/subtitle left, the global header actions right)
 * without the page-level width/padding wrapper — for surfaces whose parent
 * already owns the bounded column (e.g. the Matters dashboard views' own
 * column).
 *
 * The top-right corner is reserved for the app's global controls — the Help
 * Centre link (LF-5290) and the theme picker (VALE-520), both owned by
 * `HeaderActions`. Any page-level primary action (e.g. "New recording")
 * relocates onto that page's own filter/toolbar row instead of living here.
 */
export function PageHeaderRow({
  title,
  subtitle,
  className
}: PageHeaderRowProps) {
  return (
    <div
      className={cn('flex items-start justify-between gap-4 py-4', className)}
    >
      <div className="min-w-0">
        <h1 className="text-20px text-foreground leading-tight font-semibold">
          {title}
        </h1>
        {!!subtitle && (
          <p className="text-13px text-muted-foreground mt-0.5">{subtitle}</p>
        )}
      </div>
      <HeaderActions />
    </div>
  );
}

/** Full-width page header: bounded column + the shared header row. */
export function PageShellHeader(props: PageHeaderRowProps) {
  return (
    <header className="bg-card px-6">
      <PageHeaderRow
        {...props}
        className={cn(PAGE_SHELL_WIDTH, props.className)}
      />
    </header>
  );
}

/**
 * Page body with the bounded content column.
 *
 * By default scrolling is owned by the shared `ScrollArea` in `LayoutWrapper`
 * (LF-5279) and this is a plain flex-fill region — no `overflow-auto` here, or
 * it would nest a second scroll container.
 *
 * `bounded` flips that for a page that wants its own scrolling (VALE-345). It
 * does NOT scroll — it becomes a bounded `flex-1 min-h-0` column, which is the
 * height a child scroll region needs in order to exist at all. The page then
 * marks its chrome `ScrollShellChrome` and its list `ListSection scrollRows`
 * (or `ScrollShellRegion`), and the scrollbar lands on the list.
 *
 * Everything inside keeps the position it had before: `px-6` and the `py-5`
 * column are untouched, so opting in moves no pixels — it only changes which
 * element owns the scrollbar. `px-6` sitting OUTSIDE the child scroll box is
 * what puts that scrollbar inside the content panel rather than at the window
 * edge. Needs a bounded height from `PageShell`.
 */
export function PageShellBody({
  children,
  className,
  bounded = false
}: {
  children: ReactNode;
  className?: string;
  /**
   * Bound this body's height (`flex-1 min-h-0`) so a child can own the page's
   * scrolling. Named for what it does: this element never scrolls itself.
   */
  bounded?: boolean;
}) {
  if (bounded) {
    return (
      <main className="bg-card flex min-h-0 flex-1 flex-col px-6">
        <div
          className={cn(
            PAGE_SHELL_WIDTH,
            'flex min-h-0 flex-1 flex-col py-5',
            className
          )}
        >
          {children}
        </div>
      </main>
    );
  }

  return (
    <main className="bg-card flex-1 px-6">
      <div className={cn(PAGE_SHELL_WIDTH, 'py-5', className)}>{children}</div>
    </main>
  );
}

interface PageShellProps extends PageHeaderRowProps {
  /**
   * Page content. Wrap list/toolbar content in `PageShellBody`; render
   * full-bleed states (e.g. first-visit heroes) directly.
   *
   * To pin a toolbar (VALE-345), keep it where it already is — the first child
   * of a `PageShellBody bounded` — and wrap it in `ScrollShellChrome`. There is
   * deliberately no `toolbar` slot on this shell: a slot above the body would
   * need its own copy of the body's `px-6` + `max-w-content` column to stay
   * aligned, and would lose the body's `py-5` top gap, so pinning the toolbar
   * would silently move it. `ScrollShellChrome` pins in place instead.
   */
  children: ReactNode;
}

/** Full page shell: header + caller-provided content below. */
export function PageShell({ children, ...header }: PageShellProps) {
  return (
    /* `min-h-0` alongside `flex-1` (VALE-345) so a `PageShellBody bounded`
       child can be bounded. A page that does not opt in still grows past the
       viewport and scrolls in the shared `ScrollArea` exactly as before. */
    <div className="bg-card flex min-h-0 flex-1 flex-col">
      <PageShellHeader {...header} />
      {children}
    </div>
  );
}

/**
 * The right-aligned action strip a list surface puts **above** its
 * `ListSection` — the page's primary action at page level, top-right, outside
 * any card (VALE-480).
 *
 * It is the list surfaces' answer to `PageHeaderRow`'s `actions` slot, for the
 * `/account/*` pages that cannot use `PageShell` at all: every one of them
 * already renders the account section's `TabHeader`, so a second page header
 * would be that chrome twice (see `team-management-view.tsx` for the full
 * reasoning). Extracted so `/account/team` and `/account/scribe` share one strip
 * rather than each holding a copy of the same five utilities — a copied
 * `justify-end gap-3 mb-4` is exactly how two pages that are meant to look
 * identical stop looking identical.
 *
 * Takes any number of children, so a control can be preceded by the short
 * muted reason it is disabled.
 */
export function ListActionStrip({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-4 flex w-full items-center justify-end gap-3',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Grey strip header above a list/table section — uppercase label left,
 * optional muted count/right label (the "INTAKE FORMS · 3 total" idiom).
 *
 * The label slot is `flex-1`, so a surface whose rows have **columns** can pass
 * a full-width node laid out on the row's own grid and get column headings in
 * this strip rather than a second bar beneath it (the Contacts list, VALE-1034).
 * For a plain text label — every other caller — growing the span changes
 * nothing: it is already left-aligned, and `justify-between` already pinned
 * `rightLabel` to the right edge. Such a surface carries its count inside its
 * own grid instead of in `rightLabel`, since a sibling `rightLabel` would take
 * width out of the grid and pull the columns out of line with the rows.
 */
export function ListSectionHeader({
  label,
  rightLabel
}: {
  label: ReactNode;
  rightLabel?: ReactNode;
}) {
  /*
    The strip's fill: the surface's own answer if it declared one, else the one
    this list's row rhythm carries (VALE-1494, VALE-1756).
  */
  const headerFill = useListHeaderFill();
  return (
    <div
      className={cn('flex items-center justify-between px-4 py-2', headerFill)}
    >
      <span className="text-13px text-muted-foreground min-w-0 flex-1 font-medium tracking-wide uppercase">
        {label}
      </span>
      {!!rightLabel && (
        <span className="text-muted-foreground text-xs font-medium">
          {rightLabel}
        </span>
      )}
    </div>
  );
}

/**
 * The shared list-section primitive (LF-4759) — the single source of truth for
 * list-row separators across the app. Renders the optional grey
 * `ListSectionHeader` strip above a `divide-y divide-slate-100` row stack, so
 * every recurring one-line row-list (matter list, Scribe, Intake, contacts)
 * gets identical separators by construction rather than by manual matching.
 *
 * Rows should be flush (no own border) and rely on this container's divider.
 * Omit `label` for a header-less divided list (e.g. a nested draggable stack).
 * Table/grid and card-style surfaces intentionally opt out — they own their
 * own separator treatment (the shadcn `Table` `border-b`, spaced cards).
 *
 * `scrollRows` makes the ROWS the scroll container while the grey label strip
 * stays above it (VALE-345). The strip is a sibling of the scroller, not a
 * sticky child of it, so the scrollbar runs alongside the rows only — it starts
 * under the strip rather than beside it, which is what tells the reader that
 * the rows are the scrolling part.
 *
 * The strip spans the full panel — its background runs behind the scrollbar
 * rather than stopping short of it. It carries no right-aligned data columns,
 * so nothing needs aligning with the rows; a surface that does have them (the
 * Inventory value columns) insets its own header content by `useScrollbarWidth`.
 *
 * Requires a bounded height from the parent (a `flex-1 min-h-0` chain). Carries
 * the same `data-scroll-shell-region` hook as `ScrollShellRegion` (VALE-421) —
 * it's the same "own scrollbar behind the pinned chrome" shape, so it needs the
 * same LF-5873 scroll-lock coverage in `globals.css`.
 */
export function ListSection({
  label,
  rightLabel,
  children,
  className,
  scrollRows = false
}: {
  /** Grey section-header strip label. Omit for a header-less divided list. */
  label?: ReactNode;
  /** Muted right-side label on the header (e.g. a total count). */
  rightLabel?: ReactNode;
  children: ReactNode;
  /** Extra classes on the outer wrapper. */
  className?: string;
  /** Make the rows scroll beneath a fixed label strip. Needs a bounded height. */
  scrollRows?: boolean;
}) {
  /*
    The rule between rows comes from the enclosing surface's rhythm
    (VALE-1494) — `shared/list-row` owns both weights, so a list cannot pick
    its own. Every surface but the AI review gets `--border-subtle`, exactly as
    before.
  */
  const { divider } = useListRowRhythm();

  if (scrollRows) {
    return (
      <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
        {!!label && (
          <div className="shrink-0">
            <ListSectionHeader label={label} rightLabel={rightLabel} />
          </div>
        )}
        <div
          data-scroll-shell-region
          className={cn('min-h-0 flex-1 divide-y overflow-y-auto', divider)}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {!!label && <ListSectionHeader label={label} rightLabel={rightLabel} />}
      <div className={cn('divide-y', divider)}>{children}</div>
    </div>
  );
}

/**
 * The shared scroll-shell primitives (VALE-345) — the single source of truth
 * for "the chrome stays put and only the list scrolls".
 *
 * The problem they solve is structural, not cosmetic. Before this, every shell
 * let one big container scroll: the page header, the toolbar and the list all
 * moved together, so on a long list the reader lost the column headings and the
 * search box within the first screenful. Sticking the chrome to the top of that
 * same container fixes what you *see*, but the scrollbar still belongs to the
 * full-height container — it runs the whole viewport behind the pinned chrome
 * and gives the reader no signal about which region actually scrolls. The only
 * way the scrollbar lines up with the list is for the list to BE the scroll
 * container, which is what these three do.
 *
 * @example
 * <ScrollShell>
 *   <ScrollShellChrome>
 *     <TabHeader title="Inventory" icon={Package} />
 *     <Toolbar />
 *   </ScrollShellChrome>
 *   <ScrollShellRegion className="px-6 pb-6">
 *     <TheLongList />
 *   </ScrollShellRegion>
 * </ScrollShell>
 *
 * Two rules, both load-bearing:
 *
 * - `ScrollShell` claims the remaining space with `flex-1`, so its parent must
 *   be a column flex container with a bounded height, and every ancestor up to
 *   the scroll host must pass that height down (`flex-1 min-h-0`). A single
 *   `min-h-*` or a missing `min-h-0` anywhere in the chain and the region grows
 *   to its content instead of being bounded, which silently hands the scrollbar
 *   back to the outer container — the exact thing this exists to prevent, and
 *   it fails invisibly.
 * - The host must NOT also scroll, or you get two scrollbars. In the matter
 *   shell that means `<main>` is `overflow-hidden`; a page that opts out of
 *   `ScrollShell` therefore has to own its own scrolling.
 */
export function ScrollShell({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      {children}
    </div>
  );
}

/**
 * The pinned region: page header, toolbar, column headings — anything that must
 * stay readable and clickable at every scroll position. `shrink-0` so a long
 * list can never squeeze it, which is also why callers should keep it short:
 * on a 600px-tall window every pixel here is taken from the list.
 */
export function ScrollShellChrome({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('shrink-0', className)}>{children}</div>;
}

/**
 * The one scrolling region — fills whatever height the chrome leaves, and owns
 * the only scrollbar on the page, sized and positioned to the list itself.
 *
 * `data-scroll-shell-region` is a stable hook for the LF-5873 scroll-lock rule
 * in `globals.css` (`body[data-scroll-locked] [data-scroll-shell-region]`) —
 * this is the element that actually scrolls, not `<main>` (VALE-421).
 */
export function ScrollShellRegion({
  children,
  className,
  ref
}: {
  children: ReactNode;
  className?: string;
  /** The scrolling element itself — needed by callers that measure it (e.g.
   *  `useScrollbarWidth`, to align a header rendered above this box). */
  ref?: Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      data-scroll-shell-region
      className={cn('min-h-0 flex-1 overflow-y-auto', className)}
    >
      {children}
    </div>
  );
}

/**
 * A whole list page: fixed title header, fixed toolbar, and a list that owns the
 * only scrollbar (VALE-345).
 *
 * This is the shape every list surface in the app already had —
 * header → toolbar → `ListSection` rows — with one thing changed: the page no
 * longer scrolls as a block. Nothing about the visual result moves; the header,
 * the toolbar and the rows keep their existing markup and spacing. Only the
 * scroll container does, from the shell down onto the rows, so the scrollbar
 * sits beside the rows at the rows' height instead of running the full page.
 *
 * Pair it with `ListSection`'s `scrollRows`, which puts the grey label strip
 * above the scroll box so the scrollbar starts under it:
 *
 * @example
 * <ListPageShell
 *   header={<PageShellHeader title="Matters" />}
 *   toolbar={<MatterFilters … />}
 * >
 *   <ListSection label="All matters" scrollRows>
 *     {rows}
 *   </ListSection>
 * </ListPageShell>
 *
 * **It must be able to bound its own height**, which means a `flex-1 min-h-0`
 * chain from the nearest bounded ancestor. In the dashboard shell that ancestor
 * is `LayoutWrapper`'s `min-h-full` column; in the matter shell it is `<main>`.
 * Miss a link in that chain and the shell silently grows to its content and
 * hands the scrollbar back to the page — the failure is invisible, so verify in
 * the browser that the rows are the only scroller.
 */
export function ListPageShell({
  header,
  toolbar,
  children,
  className,
  contentClassName
}: {
  /** Fixed page header — typically `PageShellHeader`. */
  header?: ReactNode;
  /** Fixed toolbar — the page's existing search / filter / action row. */
  toolbar?: ReactNode;
  /** The list. Give it `ListSection scrollRows` so the rows do the scrolling. */
  children: ReactNode;
  /** Extra classes on the outer shell. */
  className?: string;
  /** Extra classes on the bounded content column below the header. */
  contentClassName?: string;
}) {
  return (
    <ScrollShell className={className}>
      {!!header && <ScrollShellChrome>{header}</ScrollShellChrome>}
      <div className={cn('flex min-h-0 flex-1 flex-col', contentClassName)}>
        {!!toolbar && <ScrollShellChrome>{toolbar}</ScrollShellChrome>}
        {children}
      </div>
    </ScrollShell>
  );
}
